import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const NewVentures = () => {
  const { language } = useLanguage();

  const ventures = [
    {
      id: 'ciano-gourmet',
      name: 'Ciano Gourmet',
      tagline: language === 'es' ? 'Catering premium + excelencia experiencial' : 'Premium catering meets experiential excellence',
      description: language === 'es' 
        ? 'Fundado en 2015 en República Dominicana. Ciano Gourmet crea experiencias gastronómicas memorables para bodas, eventos corporativos y celebraciones íntimas. Ahora sirviendo California y el Bay Area.'
        : 'Founded in 2015 in the Dominican Republic. Ciano Gourmet creates memorable culinary experiences for weddings, corporate events, and intimate celebrations. Now serving California and the Bay Area.',
      founded: '2015',
      locations: ['Dominican Republic', 'California, USA'],
      url: '/ventures/ciano-gourmet',
      image: 'https://images.unsplash.com/photo-1555244162-803834f70033?w=800',
    },
    {
      id: 'ciano-bites',
      name: 'Ciano Bites',
      tagline: language === 'es' ? 'Catering corporativo moderno, reimaginado' : 'Modern corporate catering, reimagined',
      description: language === 'es'
        ? 'Diseñado para equipos que valoran la calidad. Ciano Bites ofrece catering corporativo premium para reuniones, eventos de oficina y experiencias de team building.'
        : 'Designed for teams that value quality. Ciano Bites offers premium corporate catering for meetings, office events, and team building experiences.',
      founded: '2022',
      locations: ['San Francisco, CA', 'Bay Area'],
      url: '/ventures/ciano-bites',
      image: 'https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=800',
    },
    {
      id: 'staffdo',
      name: 'StaffDO',
      tagline: language === 'es' ? 'Marketplace de talento de República Dominicana' : 'Dominican Republic staffing marketplace',
      description: language === 'es'
        ? 'StaffDO conecta empresas con talento verificado en República Dominicana. Una plataforma que ayuda a las empresas a contratar más rápido con más claridad.'
        : 'StaffDO connects businesses with verified talent in the Dominican Republic. A platform that helps companies hire faster with more clarity.',
      founded: '2023',
      locations: ['Dominican Republic'],
      url: '/ventures/staffdo',
      image: null,
    },
    {
      id: 'kora-os',
      name: 'Kora OS',
      tagline: language === 'es' ? 'Sistema operativo para hospitalidad' : 'Operating system for hospitality',
      description: language === 'es'
        ? 'Kora OS es el sistema para operaciones complejas. Clientes, proyectos, eventos, inventario, marketing y finanzas. Todo conectado. Construido para fundadores.'
        : 'Kora OS is the system for complex operations. Clients, projects, events, inventory, marketing, and finance. All connected. Built for founders.',
      founded: '2024',
      locations: ['Global (SaaS)'],
      url: '/ventures/kora-os',
      image: null,
    },
  ];

  return (
    <div className="bg-vainilla pt-32">
      {/* Hero */}
      <section className="section-padding px-6 bg-vainilla">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-serif text-5xl md:text-7xl font-bold text-stone-900 mb-6">
            {language === 'es' ? 'Construyendo Lo Que Importa' : 'Building What Matters'}
          </h1>
          <p className="text-xl md:text-2xl text-stone-600">
            {language === 'es' 
              ? 'Cuatro ventures. Una misión: Excelencia en hospitalidad, operaciones y conexión humana.'
              : 'Four ventures. One mission: Excellence in hospitality, operations, and human connection.'}
          </p>
        </div>
      </section>

      {/* Ventures Grid */}
      <section className="section-padding px-6">
        <div className="max-w-7xl mx-auto space-y-16">
          {ventures.map((venture, idx) => (
            <Card key={venture.id} className={`border-stone-200 overflow-hidden ${idx % 2 === 0 ? 'bg-white' : 'bg-cream'}`}>
              <div className="grid md:grid-cols-2 gap-8">
                {/* Image */}
                <div className={`aspect-[4/3] md:aspect-auto bg-perla overflow-hidden ${idx % 2 === 0 ? 'order-1' : 'order-2'}`}>
                  {venture.image ? (
                    <img src={venture.image} alt={venture.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="font-serif text-4xl text-stone-400">{venture.name.charAt(0)}</span>
                    </div>
                  )}
                </div>
                
                {/* Content */}
                <div className={`p-12 flex flex-col justify-center ${idx % 2 === 0 ? 'order-2' : 'order-1'}`}>
                  <h2 className="font-serif text-4xl font-bold text-stone-900 mb-3">
                    {venture.name}
                  </h2>
                  <p className="text-lg text-stone-600 font-medium mb-6">
                    {venture.tagline}
                  </p>
                  <p className="text-stone-600 leading-relaxed mb-6">
                    {venture.description}
                  </p>
                  <div className="space-y-2 mb-8">
                    <p className="text-sm text-stone-500">
                      <span className="font-semibold text-stone-700">{language === 'es' ? 'Fundado:' : 'Founded:'}</span> {venture.founded}
                    </p>
                    <p className="text-sm text-stone-500">
                      <span className="font-semibold text-stone-700">{language === 'es' ? 'Ubicaciones:' : 'Locations:'}</span> {venture.locations.join(', ')}
                    </p>
                  </div>
                  <Link to={venture.url} className="text-stone-900 font-semibold inline-flex items-center hover:underline">
                    {language === 'es' ? 'Ver Más' : 'Learn More'} <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding px-6 bg-cream">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-stone-900 mb-6">
            {language === 'es' ? '¿Interesada en Colaborar?' : 'Interested in Collaboration?'}
          </h2>
          <p className="text-xl text-stone-600 mb-10">
            {language === 'es' 
              ? 'Contáctanos para discutir oportunidades de alianza o consultas de servicios.'
              : 'Reach out to discuss partnership opportunities or service inquiries.'}
          </p>
          <Link to="/contact">
            <button className="bg-stone-900 text-white hover:bg-stone-800 px-12 py-6 text-lg font-semibold transition-colors">
              {language === 'es' ? 'Contáctanos' : 'Get in Touch'}
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default NewVentures;
